import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LayingTypeEnum } from 'src/app/enum/decking-boards/laying-type.enum';
import { TypeLocationEnum } from 'src/app/enum/decking-boards/type-location.enum';
import { ShapeTypeEnum } from 'src/app/enum/shape-type.enum';
import { ConfigurationSide } from 'src/app/models/configurators-2d/applications/decking-boards/configuration-side';
import { LayingType } from 'src/app/models/configurators-2d/applications/decking-boards/laying-type';
import { Shape } from 'src/app/models/configurators-2d/applications/decking-boards/shape';
import { DeckingBoardsService } from 'src/app/services/configurators-2d/applications/decking-boards.service';
import { DeckingBoardsConfiguratorItem } from '../../configurator/applications/decking-boards-configurator-item';
import { IBaseComponent } from '../../configurator/i-base-component';
import { DeckingBoardsPreviewComponent } from '../preview/preview.component';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { CheckConfigurationSides } from 'src/app/models/configurators-2d/applications/decking-boards/configuration';

@Component({
  selector: 'ng-decking-boards-decking-boards-configuration',
  templateUrl: './decking-boards-configuration.component.html',
})
export class DeckingBoardsConfigurationComponent
  extends DeckingBoardsConfiguratorItem
  implements OnInit, IBaseComponent
{
  shapes: Shape[];
  layingTypes: LayingType[];

  currentLang: string;

  //URL de base des images
  baseImgURL: string = undefined;

  //Sous étape
  subStep: number = 1;

  maxStraightLength: number = 0;
  maxConfigurationLength: number = null;
  availableVerticalPattern: String;

  preview: DeckingBoardsPreviewComponent;

  constructor(
    deckingBoardsService: DeckingBoardsService,
    private translate: TranslateService,
    configurators2DComponent: Configurators2DComponent
  ) {
    super(deckingBoardsService);
    this.baseImgURL = this.service.apiUrl;
    this.preview = configurators2DComponent.preview as DeckingBoardsPreviewComponent;
  }

  ngOnInit(): void {
    this.currentLang = this.translate.getBrowserLang();

    let deckingBoards = [];

    Promise.all([
      this.service.getShapes().then(shapes => (this.shapes = shapes)),
      this.service.getLayingTypes().then(layingTypes => (this.layingTypes = layingTypes)),
      this.service.getDeckingBoards().then(db => (deckingBoards = db)),
    ]).then(() => {
      this.maxStraightLength = Math.max(...deckingBoards.map(db => db.length));
      this.availableVerticalPattern =
        this.translate.instant('decking-boards.configuration.available-vertical-pattern') +
        ' ' +
        this.maxStraightLength +
        'cm';

      if (this.configuration.shapeId) {
        this.configuration.shape = this.shapes.find(x => x.id == this.configuration.shapeId);
      }

      if (this.configuration.layingTypeId) {
        this.configuration.layingType = this.layingTypes.find(x => x.id == this.configuration.layingTypeId);
      }

      this.preview.update();
    });
  }

  /**
   * Fonction d'affichage des sous menus
   * @param subStep sous étape
   */
  toggleCollapse(subStep: number) {
    if (subStep === this.subStep) {
      this.subStep = 0;
    } else if (subStep === 1 || (subStep > 1 && this.configuration.shape)) {
      this.subStep = subStep;
    }
  }

  /**
   * Fonction d'affichage de la sélection de la forme
   * @param id de la forme
   * @returns boolean
   */
  isShapeSelected(id: number) {
    return this.configuration.shape?.id == id;
  }

  /**
   * Fonction de sélection de le forme
   * @param shape
   */
  async selectShape(shape: Shape): Promise<void> {
    this.configuration.shape = shape;
    this.createSides(shape.sidesCount);
    this.toggleCollapse(2);

    await this.preview.update();
  }

  /**
   * Fonction d'affichage de la sélection de la type de pose
   * @param id de la type de pose
   * @returns boolean
   */
  isLayingTypeSelected(layingType: LayingType) {
    if (!this.isLayingTypeEnable(layingType.type)) {
      return this.configuration.layingType?.id == layingType.id;
    }
  }

  /**
   * Fonction d'affichage pour si un type de pose est disponible ou non.
   * @param type
   * @returns
   */
  isLayingTypeEnable(type: number) {
    if (type === LayingTypeEnum.Straight) {
      return !this.isStraightLayingPossible();
    } else {
      return false;
    }
  }

  /**
   * Fonction de sélection de la type de pose
   * @param layingType
   */
  async selectLayingType(layingType: LayingType): Promise<void> {
    if (this.configuration.shape !== null && !this.isLayingTypeEnable(layingType.type)) {
      this.configuration.layingType = layingType;
      await this.preview.update();
    }
  }

  /**
   * Créer une lettre en fonction du chiffre
   * @param index
   */
  getLetter(index: number) {
    const code = 'A'.charCodeAt(0);
    return String.fromCharCode(code + index);
  }

  getConfigurationSides(): ConfigurationSide[] {
    return this.configuration.configurationSides.sort((a, b) => a.name.localeCompare(b.name));
  }

  createSides(sideNumber: number): ConfigurationSide[] {
    let sides: ConfigurationSide[] = [];
    for (let i = 0; i < sideNumber; i++) {
      let side = new ConfigurationSide();
      side.name = this.getLetter(i);
      side.length = 0;
      side.hasFinishing = true;
      side.finishingLength = 0;
      sides.push(side);
    }

    this.configuration.configurationSides = sides;
    return this.configuration.configurationSides;
  }

  /**
   * Mets à jour l'objet side dans la configuration
   * @param side
   */
  updateSide(side: ConfigurationSide, updateLength: boolean, event: any) {
    if (updateLength) {
      side.length = this.convertValueInCentimeter(event.target.value);
    }

    if (this.configuration.shape !== null) {
      if (side.hasFinishing) {
        side.finishingLength = side.length;
      }

      let sides = this.configuration.configurationSides.filter(s => s.name !== side.name);
      sides.push(side);
      sides.sort((a, b) => a.name.localeCompare(b.name));

      if (side.name === 'A') {
        if (this.configuration.layingType?.type === LayingTypeEnum.Straight && !this.isStraightLayingPossible()) {
          this.configuration.layingType = null;
        }
      }

      this.configuration.configurationSides = sides;
    }
  }

  calculateLength(side: ConfigurationSide): number {
    if (!this.configuration.shape.sidesNeeded.includes(side.name)) {
      let value = 0;

      switch (this.configuration.shape.shapeType) {
        case ShapeTypeEnum.rectangle:
          switch (side.name) {
            case 'C':
              value = this.configuration.configurationSides.find(s => s.name === 'A').length;
              break;

            case 'D':
              value = this.configuration.configurationSides.find(s => s.name === 'B').length;
              break;
          }
          break;

        case ShapeTypeEnum.l:
          switch (side.name) {
            case 'B':
              value =
                this.configuration.configurationSides.find(s => s.name === 'F').length -
                this.configuration.configurationSides.find(s => s.name === 'D').length;
              break;

            case 'E':
              value =
                this.configuration.configurationSides.find(s => s.name === 'A').length -
                this.configuration.configurationSides.find(s => s.name === 'C').length;
              break;
          }
          break;

        case ShapeTypeEnum.reversedL:
          switch (side.name) {
            case 'C':
              value =
                this.configuration.configurationSides.find(s => s.name === 'A').length -
                this.configuration.configurationSides.find(s => s.name === 'E').length;
              break;

            case 'F':
              value =
                this.configuration.configurationSides.find(s => s.name === 'B').length -
                this.configuration.configurationSides.find(s => s.name === 'D').length;
              break;
          }
          break;

        case ShapeTypeEnum.t:
          switch (side.name) {
            case 'C':
              value =
                (this.configuration.configurationSides.find(s => s.name === 'A').length -
                  this.configuration.configurationSides.find(s => s.name === 'E').length) /
                2;
              break;

            case 'F':
              value = this.configuration.configurationSides.find(s => s.name === 'D').length;
              break;

            case 'G':
              value =
                (this.configuration.configurationSides.find(s => s.name === 'A').length -
                  this.configuration.configurationSides.find(s => s.name === 'E').length) /
                2;
              break;

            case 'H':
              value = this.configuration.configurationSides.find(s => s.name === 'B').length;
              break;
          }
          break;

        case ShapeTypeEnum.u:
          switch (side.name) {
            case 'E':
              value =
                this.configuration.configurationSides.find(s => s.name === 'A').length -
                this.configuration.configurationSides.find(s => s.name === 'C').length -
                this.configuration.configurationSides.find(s => s.name === 'G').length;
              break;

            case 'F':
              value =
                this.configuration.configurationSides.find(s => s.name === 'H').length -
                (this.configuration.configurationSides.find(s => s.name === 'B').length -
                  this.configuration.configurationSides.find(s => s.name === 'D').length);
              break;
          }
          break;

        case ShapeTypeEnum.poolOutline:
          switch (side.name) {
            case 'C':
              value = this.configuration.configurationSides.find(s => s.name === 'A').length;
              break;

            case 'D':
              value = this.configuration.configurationSides.find(s => s.name === 'B').length;
              break;

            case 'G':
              value = this.configuration.configurationSides.find(s => s.name === 'E').length;
              break;

            case 'H':
              value = this.configuration.configurationSides.find(s => s.name === 'F').length;
              break;
          }
          break;
      }

      side.length = value;
      side.finishingLength = value;
    }

    return this.convertValueInMeter(side.length);
  }

  isNextButtonActive(): boolean {
    return (
      this.configuration.shape != null &&
      this.configuration.layingType != null &&
      this.configuration?.configurationSides.find(x => x.length <= 0) == null &&
      CheckConfigurationSides(this.configuration)
    );
  }

  checkPageChangeCapability(): boolean {
    return (
      this.configuration.location != null &&
      (this.configuration.location.locationType === TypeLocationEnum.concrete ||
        this.configuration.raisedHeight != null)
    );
  }

  /**
   * Test si la pose droite est possible si la largeur max de la config est inférieure à la plus grande lame possible
   */
  isStraightLayingPossible(): boolean {
    return this.configuration.sideALength <= this.maxStraightLength;
  }

  changeFinishingLength(side: ConfigurationSide, event: any) {
    side.finishingLength = this.convertValueInCentimeter(event.target.value);
  }

  convertValueInMeter(value: number): number {
    return !value ? 0 : value / 100;
  }

  convertValueInCentimeter(value: number): number {
    return !value ? 0 : Math.trunc(value * 100);
  }
}
